/**
 * EmailTemplates – Página de Modelos Prontos de E-mail (estilo RD Station)
 * Exibe templates do sistema + templates salvos pelo usuário.
 * Permite buscar, visualizar preview e usar template no editor.
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { LEAD_CONVERSA_TEMPLATES } from "@/data/emailSystemTemplates";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Search,
  Plus,
  Code2,
  LayoutTemplate,
  Eye,
  FileText,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { ImportHtmlDialog } from "@/components/email/ImportHtmlDialog";

// ─── System (predefined) templates ──────────────────────────────────────────

interface SystemTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  thumbnail: string; // emoji/icon placeholder
  blocks: unknown[];
  html: string;
  subject: string;
}

const SYSTEM_TEMPLATES: SystemTemplate[] = [
  {
    id: "single_column",
    name: "1 Coluna",
    category: "Básico",
    description: "Layout simples com logo, texto e botão CTA",
    thumbnail: "📄",
    subject: "Sua newsletter chegou!",
    blocks: [
      { type: "image", props: { imageUrl: "", alt: "Logo da empresa", alignment: "center", width: 180 } },
      { type: "text", props: { content: "<h1 style='text-align:center;color:#1a1a2e;'>Título do seu e-mail</h1><p style='color:#555;font-size:16px;line-height:1.6;'>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam.</p>", fontSize: 16, alignment: "left" } },
      { type: "button", props: { text: "Saiba mais", url: "#", backgroundColor: "#6366f1", textColor: "#ffffff", borderRadius: 6, alignment: "center", fullWidth: false } },
      { type: "spacer", props: { height: 20 } },
      { type: "divider", props: { color: "#e5e7eb", thickness: 1, style: "solid" } },
      { type: "text", props: { content: "<p style='text-align:center;font-size:12px;color:#999;'>© 2026 Sua Empresa. Todos os direitos reservados.</p>", fontSize: 12, alignment: "center" } },
    ],
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f4f4f5;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;"><tr><td align="center" style="padding:40px 20px;"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;"><tr><td style="padding:32px 40px;text-align:center;"><img src="" alt="Logo" style="max-width:180px;height:auto;"></td></tr><tr><td style="padding:0 40px 24px;"><h1 style="margin:0 0 16px;color:#1a1a2e;font-size:24px;text-align:center;">Título do seu e-mail</h1><p style="margin:0;color:#555;font-size:16px;line-height:1.6;">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p></td></tr><tr><td style="padding:0 40px 32px;text-align:center;"><a href="#" style="display:inline-block;background:#6366f1;color:#fff;padding:12px 32px;border-radius:6px;text-decoration:none;font-weight:600;">Saiba mais</a></td></tr><tr><td style="padding:0 40px;"><hr style="border:none;border-top:1px solid #e5e7eb;margin:0;"></td></tr><tr><td style="padding:24px 40px;text-align:center;"><p style="margin:0;font-size:12px;color:#999;">© 2026 Sua Empresa. Todos os direitos reservados.</p></td></tr></table></td></tr></table></body></html>`,
  },
  {
    id: "simple_cta",
    name: "Minimalista",
    category: "Básico",
    description: "Logo, texto direto, botão CTA e redes sociais",
    thumbnail: "✨",
    subject: "Uma mensagem para você",
    blocks: [
      { type: "image", props: { imageUrl: "", alt: "Logo", alignment: "center", width: 140 } },
      { type: "spacer", props: { height: 16 } },
      { type: "text", props: { content: "<h2 style='text-align:center;color:#111;'>Olá!</h2><p style='text-align:center;color:#666;font-size:15px;line-height:1.6;'>Temos uma novidade especial para você. Clique no botão abaixo para conferir.</p>", fontSize: 15, alignment: "center" } },
      { type: "button", props: { text: "Ver novidade", url: "#", backgroundColor: "#0ea5e9", textColor: "#ffffff", borderRadius: 24, alignment: "center", fullWidth: false } },
      { type: "spacer", props: { height: 24 } },
      { type: "social", props: { networks: [{ name: "Instagram", url: "#" }, { name: "Facebook", url: "#" }, { name: "LinkedIn", url: "#" }], style: "colored", alignment: "center" } },
      { type: "text", props: { content: "<p style='text-align:center;font-size:11px;color:#aaa;'>Se não deseja mais receber nossos e-mails, <a href='#' style='color:#aaa;'>cancele sua inscrição</a>.</p>", fontSize: 11, alignment: "center" } },
    ],
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f9fafb;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;"><tr><td align="center" style="padding:40px 20px;"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);"><tr><td style="padding:36px 40px 16px;text-align:center;"><img src="" alt="Logo" style="max-width:140px;height:auto;"></td></tr><tr><td style="padding:16px 48px 8px;text-align:center;"><h2 style="margin:0 0 12px;color:#111;font-size:22px;">Olá!</h2><p style="margin:0;color:#666;font-size:15px;line-height:1.6;">Temos uma novidade especial para você. Clique no botão abaixo para conferir.</p></td></tr><tr><td style="padding:20px 40px 28px;text-align:center;"><a href="#" style="display:inline-block;background:#0ea5e9;color:#fff;padding:12px 36px;border-radius:24px;text-decoration:none;font-weight:600;font-size:15px;">Ver novidade</a></td></tr><tr><td style="padding:0 40px 24px;text-align:center;font-size:22px;">📸 📘 💼</td></tr><tr><td style="padding:0 40px 28px;text-align:center;"><p style="margin:0;font-size:11px;color:#aaa;">Se não deseja mais receber nossos e-mails, <a href="#" style="color:#aaa;">cancele sua inscrição</a>.</p></td></tr></table></td></tr></table></body></html>`,
  },
  {
    id: "personalized_message",
    name: "Mensagem Simples",
    category: "Básico",
    description: "Texto personalizado com variáveis de contato",
    thumbnail: "💬",
    subject: "Olá, {{primeiro_nome}}!",
    blocks: [
      { type: "text", props: { content: "<p style='color:#333;font-size:16px;line-height:1.7;'>Olá <strong>{{primeiro_nome}}</strong>,</p><p style='color:#333;font-size:16px;line-height:1.7;'>Espero que esteja tudo bem! Estamos entrando em contato para compartilhar uma atualização importante.</p><p style='color:#333;font-size:16px;line-height:1.7;'>Se tiver alguma dúvida, não hesite em nos responder.</p><p style='color:#333;font-size:16px;line-height:1.7;'>Abraços,<br/><strong>Equipe {{empresa}}</strong></p>", fontSize: 16, alignment: "left" } },
      { type: "divider", props: { color: "#e5e7eb", thickness: 1, style: "solid" } },
      { type: "text", props: { content: "<p style='font-size:12px;color:#999;text-align:center;'>Enviado por {{empresa}} · <a href='#' style='color:#999;'>Cancelar inscrição</a></p>", fontSize: 12, alignment: "center" } },
    ],
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f4f4f5;font-family:Georgia,serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;"><tr><td align="center" style="padding:40px 20px;"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;"><tr><td style="padding:40px 48px;"><p style="margin:0 0 16px;color:#333;font-size:16px;line-height:1.7;">Olá <strong>{{primeiro_nome}}</strong>,</p><p style="margin:0 0 16px;color:#333;font-size:16px;line-height:1.7;">Espero que esteja tudo bem! Estamos entrando em contato para compartilhar uma atualização importante.</p><p style="margin:0 0 16px;color:#333;font-size:16px;line-height:1.7;">Se tiver alguma dúvida, não hesite em nos responder.</p><p style="margin:0;color:#333;font-size:16px;line-height:1.7;">Abraços,<br/><strong>Equipe {{empresa}}</strong></p></td></tr><tr><td style="padding:0 48px;"><hr style="border:none;border-top:1px solid #e5e7eb;margin:0;"></td></tr><tr><td style="padding:20px 48px;text-align:center;"><p style="margin:0;font-size:12px;color:#999;">Enviado por {{empresa}} · <a href="#" style="color:#999;">Cancelar inscrição</a></p></td></tr></table></td></tr></table></body></html>`,
  },
  {
    id: "newsletter",
    name: "Newsletter",
    category: "Marketing",
    description: "Múltiplas seções com imagens e links para artigos",
    thumbnail: "📰",
    subject: "Newsletter - As novidades da semana",
    blocks: [
      { type: "image", props: { imageUrl: "", alt: "Logo Newsletter", alignment: "center", width: 160 } },
      { type: "text", props: { content: "<h1 style='text-align:center;color:#1e293b;font-size:28px;'>Newsletter Semanal</h1><p style='text-align:center;color:#64748b;font-size:14px;'>As melhores novidades da semana, direto para você.</p>", fontSize: 14, alignment: "center" } },
      { type: "divider", props: { color: "#e2e8f0", thickness: 1, style: "solid" } },
      { type: "image", props: { imageUrl: "", alt: "Artigo 1", alignment: "center", width: 520 } },
      { type: "text", props: { content: "<h2 style='color:#1e293b;font-size:20px;margin:0 0 8px;'>Artigo em Destaque</h2><p style='color:#475569;font-size:15px;line-height:1.6;'>Resumo do artigo principal da semana. Clique para ler o conteúdo completo no blog.</p>", fontSize: 15, alignment: "left" } },
      { type: "button", props: { text: "Ler artigo completo", url: "#", backgroundColor: "#1e293b", textColor: "#ffffff", borderRadius: 6, alignment: "left", fullWidth: false } },
      { type: "divider", props: { color: "#e2e8f0", thickness: 1, style: "solid" } },
      { type: "text", props: { content: "<h3 style='color:#1e293b;font-size:17px;margin:0 0 8px;'>Mais Artigos</h3><p style='color:#475569;font-size:14px;line-height:1.6;'>📌 <a href='#' style='color:#2563eb;'>Como melhorar sua estratégia de marketing</a><br/>📌 <a href='#' style='color:#2563eb;'>5 tendências para ficar de olho</a><br/>📌 <a href='#' style='color:#2563eb;'>Guia completo de automação</a></p>", fontSize: 14, alignment: "left" } },
      { type: "spacer", props: { height: 16 } },
      { type: "divider", props: { color: "#e2e8f0", thickness: 1, style: "solid" } },
      { type: "social", props: { networks: [{ name: "Instagram", url: "#" }, { name: "Twitter", url: "#" }, { name: "LinkedIn", url: "#" }], style: "colored", alignment: "center" } },
      { type: "text", props: { content: "<p style='text-align:center;font-size:11px;color:#94a3b8;'>© 2026 Sua Empresa · <a href='#' style='color:#94a3b8;'>Cancelar inscrição</a></p>", fontSize: 11, alignment: "center" } },
    ],
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;"><tr><td align="center" style="padding:40px 20px;"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;"><tr><td style="padding:32px 40px 16px;text-align:center;"><img src="" alt="Logo" style="max-width:160px;height:auto;"></td></tr><tr><td style="padding:0 40px 20px;text-align:center;"><h1 style="margin:0 0 8px;color:#1e293b;font-size:28px;">Newsletter Semanal</h1><p style="margin:0;color:#64748b;font-size:14px;">As melhores novidades da semana, direto para você.</p></td></tr><tr><td style="padding:0 40px;"><hr style="border:none;border-top:1px solid #e2e8f0;"></td></tr><tr><td style="padding:20px 40px;"><img src="" alt="Artigo" style="width:100%;height:auto;border-radius:6px;background:#e2e8f0;min-height:200px;"></td></tr><tr><td style="padding:0 40px 16px;"><h2 style="margin:0 0 8px;color:#1e293b;font-size:20px;">Artigo em Destaque</h2><p style="margin:0 0 16px;color:#475569;font-size:15px;line-height:1.6;">Resumo do artigo principal da semana. Clique para ler o conteúdo completo no blog.</p><a href="#" style="display:inline-block;background:#1e293b;color:#fff;padding:10px 24px;border-radius:6px;text-decoration:none;font-weight:600;font-size:14px;">Ler artigo completo</a></td></tr><tr><td style="padding:20px 40px 0;"><hr style="border:none;border-top:1px solid #e2e8f0;"></td></tr><tr><td style="padding:20px 40px;"><h3 style="margin:0 0 12px;color:#1e293b;font-size:17px;">Mais Artigos</h3><p style="margin:0;color:#475569;font-size:14px;line-height:2;">📌 <a href="#" style="color:#2563eb;text-decoration:none;">Como melhorar sua estratégia de marketing</a><br/>📌 <a href="#" style="color:#2563eb;text-decoration:none;">5 tendências para ficar de olho</a><br/>📌 <a href="#" style="color:#2563eb;text-decoration:none;">Guia completo de automação</a></p></td></tr><tr><td style="padding:8px 40px;"><hr style="border:none;border-top:1px solid #e2e8f0;"></td></tr><tr><td style="padding:16px 40px;text-align:center;font-size:20px;">📸 🐦 💼</td></tr><tr><td style="padding:0 40px 28px;text-align:center;"><p style="margin:0;font-size:11px;color:#94a3b8;">© 2026 Sua Empresa · <a href="#" style="color:#94a3b8;">Cancelar inscrição</a></p></td></tr></table></td></tr></table></body></html>`,
  },
  {
    id: "order_confirmation",
    name: "Confirmação de Pedido",
    category: "Transacional",
    description: "Dados do pedido com tabela de itens e total",
    thumbnail: "🛒",
    subject: "Pedido #{{numero_pedido}} confirmado!",
    blocks: [
      { type: "text", props: { content: "<div style='text-align:center;'><span style='font-size:48px;'>✅</span></div><h1 style='text-align:center;color:#16a34a;font-size:24px;'>Pedido Confirmado!</h1><p style='text-align:center;color:#555;font-size:15px;'>Obrigado pela sua compra, <strong>{{primeiro_nome}}</strong>. Seu pedido <strong>#{{numero_pedido}}</strong> foi confirmado.</p>", fontSize: 15, alignment: "center" } },
      { type: "divider", props: { color: "#e5e7eb", thickness: 1, style: "solid" } },
      { type: "text", props: { content: "<table style='width:100%;border-collapse:collapse;font-size:14px;'><thead><tr style='border-bottom:2px solid #e5e7eb;'><th style='text-align:left;padding:8px 0;color:#333;'>Item</th><th style='text-align:center;padding:8px 0;color:#333;'>Qtd</th><th style='text-align:right;padding:8px 0;color:#333;'>Preço</th></tr></thead><tbody><tr style='border-bottom:1px solid #f3f4f6;'><td style='padding:10px 0;color:#555;'>Produto Exemplo 1</td><td style='padding:10px 0;text-align:center;color:#555;'>1</td><td style='padding:10px 0;text-align:right;color:#555;'>R$ 99,90</td></tr><tr style='border-bottom:1px solid #f3f4f6;'><td style='padding:10px 0;color:#555;'>Produto Exemplo 2</td><td style='padding:10px 0;text-align:center;color:#555;'>2</td><td style='padding:10px 0;text-align:right;color:#555;'>R$ 49,90</td></tr></tbody><tfoot><tr><td colspan='2' style='padding:12px 0;text-align:right;font-weight:700;color:#111;'>Total:</td><td style='padding:12px 0;text-align:right;font-weight:700;color:#111;font-size:16px;'>R$ 199,70</td></tr></tfoot></table>", fontSize: 14, alignment: "left" } },
      { type: "divider", props: { color: "#e5e7eb", thickness: 1, style: "solid" } },
      { type: "button", props: { text: "Acompanhar pedido", url: "#", backgroundColor: "#16a34a", textColor: "#ffffff", borderRadius: 6, alignment: "center", fullWidth: true } },
      { type: "text", props: { content: "<p style='text-align:center;font-size:12px;color:#999;margin-top:16px;'>Dúvidas? Responda este e-mail ou entre em contato pelo nosso suporte.</p>", fontSize: 12, alignment: "center" } },
    ],
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f4f4f5;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;"><tr><td align="center" style="padding:40px 20px;"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;"><tr><td style="padding:40px 40px 16px;text-align:center;"><div style="font-size:48px;">✅</div><h1 style="margin:16px 0 8px;color:#16a34a;font-size:24px;">Pedido Confirmado!</h1><p style="margin:0;color:#555;font-size:15px;">Obrigado pela sua compra, <strong>{{primeiro_nome}}</strong>. Seu pedido <strong>#{{numero_pedido}}</strong> foi confirmado.</p></td></tr><tr><td style="padding:0 40px;"><hr style="border:none;border-top:1px solid #e5e7eb;"></td></tr><tr><td style="padding:20px 40px;"><table style="width:100%;border-collapse:collapse;font-size:14px;"><thead><tr style="border-bottom:2px solid #e5e7eb;"><th style="text-align:left;padding:8px 0;color:#333;">Item</th><th style="text-align:center;padding:8px 0;color:#333;">Qtd</th><th style="text-align:right;padding:8px 0;color:#333;">Preço</th></tr></thead><tbody><tr style="border-bottom:1px solid #f3f4f6;"><td style="padding:10px 0;color:#555;">Produto Exemplo 1</td><td style="padding:10px 0;text-align:center;color:#555;">1</td><td style="padding:10px 0;text-align:right;color:#555;">R$ 99,90</td></tr><tr style="border-bottom:1px solid #f3f4f6;"><td style="padding:10px 0;color:#555;">Produto Exemplo 2</td><td style="padding:10px 0;text-align:center;color:#555;">2</td><td style="padding:10px 0;text-align:right;color:#555;">R$ 49,90</td></tr></tbody><tfoot><tr><td colspan="2" style="padding:12px 0;text-align:right;font-weight:700;color:#111;">Total:</td><td style="padding:12px 0;text-align:right;font-weight:700;color:#111;font-size:16px;">R$ 199,70</td></tr></tfoot></table></td></tr><tr><td style="padding:0 40px;"><hr style="border:none;border-top:1px solid #e5e7eb;"></td></tr><tr><td style="padding:20px 40px;text-align:center;"><a href="#" style="display:block;background:#16a34a;color:#fff;padding:14px 32px;border-radius:6px;text-decoration:none;font-weight:600;text-align:center;">Acompanhar pedido</a></td></tr><tr><td style="padding:8px 40px 32px;text-align:center;"><p style="margin:0;font-size:12px;color:#999;">Dúvidas? Responda este e-mail ou entre em contato pelo nosso suporte.</p></td></tr></table></td></tr></table></body></html>`,
  },
  {
    id: "welcome",
    name: "Boas-vindas",
    category: "Onboarding",
    description: "E-mail de onboarding com áreas de interesse",
    thumbnail: "👋",
    subject: "Bem-vindo(a) à {{empresa}}!",
    blocks: [
      { type: "text", props: { content: "<div style='text-align:center;background:linear-gradient(135deg,#6366f1,#8b5cf6);padding:40px 32px;border-radius:8px;'><span style='font-size:48px;'>👋</span><h1 style='color:#fff;font-size:28px;margin:16px 0 8px;'>Bem-vindo(a)!</h1><p style='color:rgba(255,255,255,0.85);font-size:16px;margin:0;'>Estamos muito felizes em ter você conosco.</p></div>", fontSize: 16, alignment: "center" } },
      { type: "spacer", props: { height: 24 } },
      { type: "text", props: { content: "<p style='color:#333;font-size:16px;line-height:1.7;'>Olá <strong>{{primeiro_nome}}</strong>,</p><p style='color:#555;font-size:15px;line-height:1.7;'>Sua conta foi criada com sucesso! Aqui estão os próximos passos para aproveitar ao máximo:</p>", fontSize: 15, alignment: "left" } },
      { type: "text", props: { content: "<div style='background:#f8fafc;border-radius:8px;padding:20px;'><p style='margin:0 0 12px;font-size:15px;color:#333;'>✅ <strong>Complete seu perfil</strong> — Adicione suas informações</p><p style='margin:0 0 12px;font-size:15px;color:#333;'>📚 <strong>Explore os recursos</strong> — Conheça todas as funcionalidades</p><p style='margin:0;font-size:15px;color:#333;'>💬 <strong>Fale conosco</strong> — Estamos aqui para ajudar</p></div>", fontSize: 15, alignment: "left" } },
      { type: "button", props: { text: "Acessar minha conta", url: "#", backgroundColor: "#6366f1", textColor: "#ffffff", borderRadius: 8, alignment: "center", fullWidth: true } },
      { type: "spacer", props: { height: 16 } },
      { type: "text", props: { content: "<p style='text-align:center;font-size:12px;color:#999;'>© 2026 {{empresa}} · <a href='#' style='color:#999;'>Cancelar inscrição</a></p>", fontSize: 12, alignment: "center" } },
    ],
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f4f4f5;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;"><tr><td align="center" style="padding:40px 20px;"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;"><tr><td style="padding:0;"><div style="background:linear-gradient(135deg,#6366f1,#8b5cf6);padding:40px 32px;text-align:center;"><span style="font-size:48px;">👋</span><h1 style="color:#fff;font-size:28px;margin:16px 0 8px;">Bem-vindo(a)!</h1><p style="color:rgba(255,255,255,0.85);font-size:16px;margin:0;">Estamos muito felizes em ter você conosco.</p></div></td></tr><tr><td style="padding:32px 40px 16px;"><p style="margin:0 0 12px;color:#333;font-size:16px;line-height:1.7;">Olá <strong>{{primeiro_nome}}</strong>,</p><p style="margin:0;color:#555;font-size:15px;line-height:1.7;">Sua conta foi criada com sucesso! Aqui estão os próximos passos para aproveitar ao máximo:</p></td></tr><tr><td style="padding:0 40px 20px;"><div style="background:#f8fafc;border-radius:8px;padding:20px;"><p style="margin:0 0 12px;font-size:15px;color:#333;">✅ <strong>Complete seu perfil</strong> — Adicione suas informações</p><p style="margin:0 0 12px;font-size:15px;color:#333;">📚 <strong>Explore os recursos</strong> — Conheça todas as funcionalidades</p><p style="margin:0;font-size:15px;color:#333;">💬 <strong>Fale conosco</strong> — Estamos aqui para ajudar</p></div></td></tr><tr><td style="padding:0 40px 32px;text-align:center;"><a href="#" style="display:block;background:#6366f1;color:#fff;padding:14px;border-radius:8px;text-decoration:none;font-weight:600;text-align:center;">Acessar minha conta</a></td></tr><tr><td style="padding:0 40px 28px;text-align:center;"><p style="margin:0;font-size:12px;color:#999;">© 2026 {{empresa}} · <a href="#" style="color:#999;">Cancelar inscrição</a></p></td></tr></table></td></tr></table></body></html>`,
  },
  {
    id: "product_launch",
    name: "Divulgação de Produto",
    category: "Marketing",
    description: "Hero image grande, descrição e botão CTA",
    thumbnail: "🚀",
    subject: "Lançamento: Conheça o novo {{produto}}!",
    blocks: [
      { type: "image", props: { imageUrl: "", alt: "Hero - Produto", alignment: "center", width: 600, link: "#" } },
      { type: "text", props: { content: "<h1 style='text-align:center;color:#111;font-size:28px;margin:0 0 8px;'>Acabou de Chegar!</h1><p style='text-align:center;color:#6366f1;font-size:14px;font-weight:600;letter-spacing:1px;text-transform:uppercase;margin:0 0 16px;'>LANÇAMENTO EXCLUSIVO</p><p style='text-align:center;color:#555;font-size:16px;line-height:1.6;'>Apresentamos o produto que você estava esperando. Design moderno, funcionalidades incríveis e um preço especial de lançamento.</p>", fontSize: 16, alignment: "center" } },
      { type: "text", props: { content: "<div style='text-align:center;background:#f8fafc;border-radius:8px;padding:20px;'><p style='margin:0 0 4px;font-size:14px;color:#999;text-decoration:line-through;'>De R$ 299,90</p><p style='margin:0;font-size:32px;font-weight:700;color:#16a34a;'>R$ 199,90</p><p style='margin:4px 0 0;font-size:13px;color:#666;'>ou 12x de R$ 16,66</p></div>", fontSize: 14, alignment: "center" } },
      { type: "button", props: { text: "Comprar agora", url: "#", backgroundColor: "#16a34a", textColor: "#ffffff", borderRadius: 8, alignment: "center", fullWidth: true } },
      { type: "spacer", props: { height: 12 } },
      { type: "text", props: { content: "<p style='text-align:center;font-size:13px;color:#999;'>Oferta por tempo limitado. Frete grátis para todo o Brasil.</p>", fontSize: 13, alignment: "center" } },
      { type: "divider", props: { color: "#e5e7eb", thickness: 1, style: "solid" } },
      { type: "text", props: { content: "<p style='text-align:center;font-size:11px;color:#aaa;'>© 2026 Sua Loja · <a href='#' style='color:#aaa;'>Cancelar inscrição</a></p>", fontSize: 11, alignment: "center" } },
    ],
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f4f4f5;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;"><tr><td align="center" style="padding:40px 20px;"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;"><tr><td style="padding:0;"><img src="" alt="Produto" style="width:100%;height:auto;display:block;background:#e2e8f0;min-height:300px;"></td></tr><tr><td style="padding:32px 40px 16px;text-align:center;"><h1 style="margin:0 0 8px;color:#111;font-size:28px;">Acabou de Chegar!</h1><p style="margin:0 0 16px;color:#6366f1;font-size:14px;font-weight:600;letter-spacing:1px;text-transform:uppercase;">LANÇAMENTO EXCLUSIVO</p><p style="margin:0;color:#555;font-size:16px;line-height:1.6;">Apresentamos o produto que você estava esperando. Design moderno, funcionalidades incríveis e um preço especial de lançamento.</p></td></tr><tr><td style="padding:16px 40px;"><div style="text-align:center;background:#f8fafc;border-radius:8px;padding:20px;"><p style="margin:0 0 4px;font-size:14px;color:#999;text-decoration:line-through;">De R$ 299,90</p><p style="margin:0;font-size:32px;font-weight:700;color:#16a34a;">R$ 199,90</p><p style="margin:4px 0 0;font-size:13px;color:#666;">ou 12x de R$ 16,66</p></div></td></tr><tr><td style="padding:20px 40px;text-align:center;"><a href="#" style="display:block;background:#16a34a;color:#fff;padding:14px;border-radius:8px;text-decoration:none;font-weight:700;text-align:center;font-size:16px;">Comprar agora</a></td></tr><tr><td style="padding:0 40px 8px;text-align:center;"><p style="margin:0;font-size:13px;color:#999;">Oferta por tempo limitado. Frete grátis para todo o Brasil.</p></td></tr><tr><td style="padding:12px 40px;"><hr style="border:none;border-top:1px solid #e5e7eb;"></td></tr><tr><td style="padding:8px 40px 28px;text-align:center;"><p style="margin:0;font-size:11px;color:#aaa;">© 2026 Sua Loja · <a href="#" style="color:#aaa;">Cancelar inscrição</a></p></td></tr></table></td></tr></table></body></html>`,
  },
  {
    id: "coupon_chat",
    name: "Chat com Cupom",
    category: "Promoção",
    description: "Estilo conversacional com cupom de desconto",
    thumbnail: "🎟️",
    subject: "Psiu... temos um presente para você!",
    blocks: [
      { type: "image", props: { imageUrl: "", alt: "Logo", alignment: "center", width: 120 } },
      { type: "text", props: { content: "<div style='background:#f0f4ff;border-radius:16px 16px 16px 4px;padding:16px 20px;margin-bottom:12px;max-width:80%;'><p style='margin:0;color:#333;font-size:15px;'>Oi! 😊 Tudo bem?</p></div><div style='background:#f0f4ff;border-radius:16px 16px 16px 4px;padding:16px 20px;max-width:80%;'><p style='margin:0;color:#333;font-size:15px;'>Notamos que faz um tempinho que você não aparece por aqui... Sentimos sua falta! 💜</p></div>", fontSize: 15, alignment: "left" } },
      { type: "spacer", props: { height: 16 } },
      { type: "text", props: { content: "<div style='background:linear-gradient(135deg,#6366f1,#a855f7);border-radius:12px;padding:28px;text-align:center;'><p style='margin:0 0 8px;font-size:14px;color:rgba(255,255,255,0.8);'>SEU CUPOM ESPECIAL</p><p style='margin:0 0 8px;font-size:40px;font-weight:800;color:#fff;letter-spacing:3px;'>VOLTA20</p><p style='margin:0;font-size:18px;color:#fff;font-weight:600;'>20% OFF em todo o site</p></div>", fontSize: 14, alignment: "center" } },
      { type: "spacer", props: { height: 16 } },
      { type: "text", props: { content: "<div style='background:#f0f4ff;border-radius:16px 16px 16px 4px;padding:16px 20px;max-width:80%;'><p style='margin:0;color:#333;font-size:15px;'>Use o cupom acima e aproveite! Válido por 7 dias 🕐</p></div>", fontSize: 15, alignment: "left" } },
      { type: "button", props: { text: "Usar meu cupom", url: "#", backgroundColor: "#6366f1", textColor: "#ffffff", borderRadius: 24, alignment: "center", fullWidth: false } },
      { type: "spacer", props: { height: 16 } },
      { type: "text", props: { content: "<p style='text-align:center;font-size:11px;color:#aaa;'>© 2026 Sua Loja · <a href='#' style='color:#aaa;'>Cancelar inscrição</a></p>", fontSize: 11, alignment: "center" } },
    ],
    html: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#f4f4f5;font-family:Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;"><tr><td align="center" style="padding:40px 20px;"><table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;"><tr><td style="padding:32px 40px 16px;text-align:center;"><img src="" alt="Logo" style="max-width:120px;height:auto;"></td></tr><tr><td style="padding:0 40px 12px;"><div style="background:#f0f4ff;border-radius:16px 16px 16px 4px;padding:16px 20px;max-width:80%;"><p style="margin:0;color:#333;font-size:15px;">Oi! 😊 Tudo bem?</p></div></td></tr><tr><td style="padding:0 40px 16px;"><div style="background:#f0f4ff;border-radius:16px 16px 16px 4px;padding:16px 20px;max-width:80%;"><p style="margin:0;color:#333;font-size:15px;">Notamos que faz um tempinho que você não aparece por aqui... Sentimos sua falta! 💜</p></div></td></tr><tr><td style="padding:0 40px;"><div style="background:linear-gradient(135deg,#6366f1,#a855f7);border-radius:12px;padding:28px;text-align:center;"><p style="margin:0 0 8px;font-size:14px;color:rgba(255,255,255,0.8);">SEU CUPOM ESPECIAL</p><p style="margin:0 0 8px;font-size:40px;font-weight:800;color:#fff;letter-spacing:3px;">VOLTA20</p><p style="margin:0;font-size:18px;color:#fff;font-weight:600;">20% OFF em todo o site</p></div></td></tr><tr><td style="padding:16px 40px;"><div style="background:#f0f4ff;border-radius:16px 16px 16px 4px;padding:16px 20px;max-width:80%;"><p style="margin:0;color:#333;font-size:15px;">Use o cupom acima e aproveite! Válido por 7 dias 🕐</p></div></td></tr><tr><td style="padding:8px 40px 24px;text-align:center;"><a href="#" style="display:inline-block;background:#6366f1;color:#fff;padding:12px 36px;border-radius:24px;text-decoration:none;font-weight:600;">Usar meu cupom</a></td></tr><tr><td style="padding:0 40px 28px;text-align:center;"><p style="margin:0;font-size:11px;color:#aaa;">© 2026 Sua Loja · <a href="#" style="color:#aaa;">Cancelar inscrição</a></p></td></tr></table></td></tr></table></body></html>`,
  },
  ...LEAD_CONVERSA_TEMPLATES,
];

// ─── Category helpers ───────────────────────────────────────────────────────

const ALL_CATEGORIES = Array.from(new Set(SYSTEM_TEMPLATES.map((t) => t.category)));

// ─── HTML Preview component (card thumbnail) ───────────────────────────────
//
// Renderiza um e-mail de 600px num iframe e escalona pela LARGURA real do
// card (medida via ResizeObserver). Antes a escala era calculada pela altura
// do container, o que deixava sobra branca à direita e cortava conteúdo
// abaixo. Agora o iframe ocupa 100% da largura disponível e a altura do card
// é mantida fixa (proporcional 4:3) para uma grade visualmente alinhada.

const PREVIEW_IFRAME_WIDTH = 600;
const PREVIEW_IFRAME_HEIGHT = 1400;
const PREVIEW_ASPECT = 1 / 1.15; // height = width * 1.15 — mostra o "topo" do e-mail

function HtmlPreview({ html }: { html: string }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  // Renderiza o iframe apenas quando o card entra (ou está prestes a entrar) na
  // viewport. Como cada iframe carrega um documento HTML completo, montar 60+ de
  // uma vez na galeria deixa a página lenta. `loading="lazy"` não funciona com
  // `srcDoc`, daí o IntersectionObserver.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const inner = innerRef.current;
    if (!wrapper || !inner) return;
    const apply = () => {
      const w = wrapper.clientWidth || 0;
      if (!w) return;
      const scale = w / PREVIEW_IFRAME_WIDTH;
      inner.style.transform = `scale(${scale})`;
      wrapper.style.height = `${w * PREVIEW_ASPECT}px`;
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(wrapper);
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    io.observe(wrapper);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
  }, []);

  return (
    <div ref={wrapperRef} className="w-full overflow-hidden bg-gray-100">
      <div
        ref={innerRef}
        style={{
          width: PREVIEW_IFRAME_WIDTH,
          height: PREVIEW_IFRAME_HEIGHT,
          transformOrigin: "top left",
        }}
      >
        {visible ? (
          <iframe
            srcDoc={html}
            title="preview"
            className="pointer-events-none border-0"
            style={{ width: PREVIEW_IFRAME_WIDTH, height: PREVIEW_IFRAME_HEIGHT }}
            sandbox=""
          />
        ) : null}
      </div>
    </div>
  );
}

// ─── Template Preview Modal ─────────────────────────────────────────────────

function PreviewModal({
  template,
  open,
  onClose,
  onUse,
}: {
  template: SystemTemplate | { id: number; name: string; subject: string; html: string; blocks?: unknown[] } | null;
  open: boolean;
  onClose: () => void;
  onUse: () => void;
}) {
  if (!template) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden mx-2 sm:mx-0">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogHeader className="p-0 mb-3">
            <DialogTitle className="text-lg pr-8">{template.name}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">Assunto: {template.subject || "(sem assunto)"}</p>
            <Button onClick={onUse} className="gap-2 shrink-0 cursor-pointer transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95">
              <Plus className="h-4 w-4" />
              Usar este template
            </Button>
          </div>
        </div>
        {/* Preview area – scale a 700px iframe to fit the modal */}
        <div className="flex-1 overflow-y-auto bg-gray-100 flex justify-center p-4 sm:p-6 min-h-0">
          <div className="w-full max-w-[680px] bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="relative w-full" style={{ paddingBottom: "130%" }}>
              <iframe
                srcDoc={template.html}
                title="Template Preview"
                className="absolute inset-0 border-0"
                style={{ width: 700, height: 1200, transform: "scale(var(--preview-scale))", transformOrigin: "top left" }}
                ref={(el) => {
                  if (el) {
                    const parent = el.parentElement;
                    if (parent) {
                      const scale = parent.clientWidth / 700;
                      el.style.setProperty("--preview-scale", String(scale));
                      parent.style.height = `${1200 * scale}px`;
                      parent.style.paddingBottom = "0";
                    }
                  }
                }}
                sandbox=""
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function EmailTemplates() {
  const [, navigate] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [previewTemplate, setPreviewTemplate] = useState<SystemTemplate | { id: number; name: string; subject: string; html: string; blocks?: unknown[] } | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Fetch user-saved templates from DB
  const { data: userTemplates = [] } = trpc.emailTemplates.list.useQuery();

  // Create campaign mutation (to use a template)
  const createCampaignMutation = trpc.emailCampaigns.create.useMutation({
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao criar")),
  });

  const updateCampaignMutation = trpc.emailCampaigns.update.useMutation({
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao atualizar")),
  });

  const importHtmlMutation = trpc.emailCampaigns.importHtml.useMutation({
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao importar HTML")),
  });

  // ─── Use template flow ────────────────────────────────────────────────────

  async function useTemplate(template: { name: string; subject: string; html: string; blocks?: unknown[] }) {
    try {
      const campaign = await createCampaignMutation.mutateAsync({
        name: `${template.name} - ${new Date().toLocaleDateString("pt-BR")}`,
      });
      if (campaign?.id) {
        await updateCampaignMutation.mutateAsync({
          id: campaign.id,
          subject: template.subject,
          contentHtml: template.html,
          contentJson: (template.blocks as any[]) ?? [],
        });
        toast.success("E-mail criado a partir do template!");
        navigate(`/emails/${campaign.id}/editor`);
      }
    } catch {
      // errors handled by mutation callbacks
    }
  }

  // Cria uma campanha vazia, importa o HTML (que passa por normalização +
  // re-hospedagem de imagens + sanitização no server) e abre o editor.
  async function handleImportHtml(html: string) {
    const campaign = await createCampaignMutation.mutateAsync({
      name: `E-mail importado - ${new Date().toLocaleDateString("pt-BR")}`,
    });
    if (!campaign?.id) return;
    const result = await importHtmlMutation.mutateAsync({ campaignId: campaign.id, html });
    // compatWarnings = problema crítico (ex: importou SPA em vez de e-mail).
    // Mostra toast persistente — usuário precisa ler antes de seguir.
    if (result.compatWarnings && result.compatWarnings.length > 0) {
      toast.error(result.compatWarnings[0], { duration: 15000 });
    } else if (result.failedImages > 0) {
      toast.warning(`E-mail importado, mas ${result.failedImages} imagem(ns) não puderam ser baixadas.`);
    } else if (result.rehostedImages > 0) {
      toast.success(`E-mail importado! ${result.rehostedImages} imagem(ns) re-hospedada(s).`);
    } else {
      toast.success("E-mail importado!");
    }
    setImportOpen(false);
    navigate(`/emails/${campaign.id}/editor`);
  }

  // ─── Filtering ────────────────────────────────────────────────────────────

  const filteredSystem = useMemo(() => {
    let list = [...SYSTEM_TEMPLATES];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q)
      );
    }
    if (categoryFilter !== "all") {
      list = list.filter((t) => t.category === categoryFilter);
    }
    return list;
  }, [searchQuery, categoryFilter]);

  const filteredUser = useMemo(() => {
    if (!searchQuery) return userTemplates;
    const q = searchQuery.toLowerCase();
    return userTemplates.filter(
      (t: any) =>
        t.name?.toLowerCase().includes(q) ||
        t.subject?.toLowerCase().includes(q)
    );
  }, [userTemplates, searchQuery]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <PermissionGuard permission="email:manage_templates">
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/emails")}
              className="shrink-0 cursor-pointer transition-all duration-150 hover:scale-110 active:scale-95"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Modelos prontos</h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                Aproveite as inspirações. É só editar o conteúdo do seu jeito.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="gap-2 cursor-pointer transition-all duration-150 hover:scale-105 active:scale-95"
              onClick={() => setImportOpen(true)}
            >
              <Code2 className="h-4 w-4" />
              Importar com HTML
            </Button>
            <Button
              className="gap-2 cursor-pointer transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
              onClick={() => useTemplate({ name: "E-mail em branco", subject: "", html: "", blocks: [] })}
              disabled={createCampaignMutation.isPending}
            >
              <Plus className="h-4 w-4" />
              {createCampaignMutation.isPending ? "Criando..." : "Criar Email em branco"}
            </Button>
          </div>
        </div>

        {/* ── Tabs: Modelos prontos | Meus templates ───────────────────── */}
        <Tabs defaultValue="system" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <TabsList>
              <TabsTrigger value="system" className="gap-2">
                <LayoutTemplate className="h-4 w-4" />
                Modelos prontos
                <Badge variant="secondary" className="ml-1 text-xs">{filteredSystem.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="mine" className="gap-2">
                <FileText className="h-4 w-4" />
                Meus templates
                <Badge variant="secondary" className="ml-1 text-xs">{filteredUser.length}</Badge>
              </TabsTrigger>
            </TabsList>

            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar template..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* ── Tab: Modelos prontos (sistema) ─────────────────────────── */}
          <TabsContent value="system" className="space-y-4 mt-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button
                variant={categoryFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setCategoryFilter("all")}
              >
                Todos
              </Button>
              {ALL_CATEGORIES.map((cat) => (
                <Button
                  key={cat}
                  variant={categoryFilter === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCategoryFilter(cat)}
                >
                  {cat}
                </Button>
              ))}
            </div>

            {filteredSystem.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground text-sm">Nenhum template encontrado para esta busca.</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredSystem.map((tpl) => (
                  <Card
                    key={tpl.id}
                    className="group flex flex-col overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                    onClick={() => {
                      setPreviewTemplate(tpl);
                      setPreviewOpen(true);
                    }}
                  >
                    <div className="relative">
                      <HtmlPreview html={tpl.html} />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Badge className="gap-1 bg-white/90 text-foreground shadow-sm hover:bg-white">
                            <Eye className="h-3 w-3" />
                            Visualizar
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-sm truncate">{tpl.name}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {tpl.description}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-[10px] shrink-0">
                          {tpl.category}
                        </Badge>
                      </div>
                      <div className="mt-auto pt-3">
                        <Button
                          size="sm"
                          className="w-full gap-2 cursor-pointer transition-all duration-150 hover:scale-[1.03] hover:shadow-md active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            useTemplate(tpl);
                          }}
                          disabled={createCampaignMutation.isPending}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Usar template
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── Tab: Meus templates ────────────────────────────────────── */}
          <TabsContent value="mine" className="mt-0">
            {filteredUser.length === 0 ? (
              <Card className="p-12 text-center">
                <FileText className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground text-sm font-medium">Nenhum template salvo</p>
                <p className="text-muted-foreground text-xs mt-1">
                  Crie um e-mail no editor e salve como template para reutilizar aqui.
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredUser.map((tpl: any) => (
                  <Card
                    key={tpl.id}
                    className="group flex flex-col overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                    onClick={() => {
                      setPreviewTemplate(tpl);
                      setPreviewOpen(true);
                    }}
                  >
                    <div className="relative">
                      <HtmlPreview html={tpl.html || ""} />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Badge className="gap-1 bg-white/90 text-foreground shadow-sm hover:bg-white">
                            <Eye className="h-3 w-3" />
                            Visualizar
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 flex-1 flex flex-col">
                      <h3 className="font-medium text-sm truncate">{tpl.name}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{tpl.subject || "(sem assunto)"}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>{tpl.blocks ? `${(tpl.blocks as any[]).length} bloco(s)` : ""}</span>
                        {tpl.updatedAt && (
                          <>
                            <span>·</span>
                            <span>{new Date(tpl.updatedAt).toLocaleDateString("pt-BR")}</span>
                          </>
                        )}
                      </div>
                      <div className="mt-auto pt-3 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2 cursor-pointer transition-all duration-150 hover:scale-[1.03] active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/emails/templates/${tpl.id}/edit`);
                          }}
                          title="Editar este template no editor"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 gap-2 cursor-pointer transition-all duration-150 hover:scale-[1.03] hover:shadow-md active:scale-95"
                          onClick={(e) => {
                            e.stopPropagation();
                            useTemplate({
                              name: tpl.name,
                              subject: tpl.subject,
                              html: tpl.html,
                              blocks: tpl.blocks as unknown[],
                            });
                          }}
                          disabled={createCampaignMutation.isPending}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Usar
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <PreviewModal
        template={previewTemplate}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onUse={() => {
          setPreviewOpen(false);
          if (previewTemplate) {
            useTemplate({
              name: previewTemplate.name,
              subject: previewTemplate.subject,
              html: previewTemplate.html,
              blocks: "blocks" in previewTemplate ? (previewTemplate.blocks as unknown[]) : [],
            });
          }
        }}
      />
      <ImportHtmlDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImportHtml}
        isSubmitting={importHtmlMutation.isPending || createCampaignMutation.isPending}
      />
    </DashboardLayout>
    </PermissionGuard>
  );
}
